import { useUserStore, type UserData } from './user.store';

/**
 * The last resolved user, kept so the shell can draw from cache.
 *
 * The loading system's placement rule: "Toolbar and nav render immediately
 * from cache - the user can navigate while content loads." The nav is filtered
 * by entitlements, and entitlements wait on the user, which the toolbar
 * resolves from the token claims and Graph after the first render. Without a
 * cache every page load drew an empty rail beside a spinner until that
 * finished.
 *
 * Seeded only for the same account (`userId` is the MSAL `localAccountId`),
 * so a second person signing in on this browser never sees the first one's
 * nav. The toolbar re-resolves on every load and overwrites the cache, so a
 * role removed since last time is gone within that one round trip - and, as
 * everywhere in the portal, the API enforces its own policy regardless of
 * what the nav shows.
 */

const KEY = 'cw-shell-user';

function read(): UserData | null {
  try {
    const raw = localStorage.getItem(KEY);

    return raw ? (JSON.parse(raw) as UserData) : null;
  } catch {
    return null;
  }
}

/** Seeds the user store from cache when it belongs to `accountId`. Call before the first render. */
export function seedUserFromCache(accountId: string | undefined): void {
  const cached = read();

  if (!accountId || !cached || cached.userId !== accountId || useUserStore.getState().userData) {
    return;
  }

  useUserStore.getState().setUserData(cached);
}

/** Keeps the cache in step with the store from here on. Returns the unsubscribe. */
export function persistUserToCache(): () => void {
  return useUserStore.subscribe((state, previous) => {
    if (state.userData === previous.userData) {
      return;
    }

    try {
      if (state.userData) {
        localStorage.setItem(KEY, JSON.stringify(state.userData));
      } else {
        localStorage.removeItem(KEY);
      }
    } catch {
      /* no storage: the shell simply waits for the user, as before */
    }
  });
}

/** Forgets the cached user - on sign-out, so the next visitor starts clean. */
export function clearUserCache(): void {
  try {
    localStorage.removeItem(KEY);
  } catch {
    /* nothing to clear */
  }
}
